import {createLocation, History, Location} from 'history'
import {ReactElement, ReactNode} from 'react'
import ReactMarkdown, {Components, defaultUrlTransform} from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {useTranslation} from 'react-i18next'
import {Link, useHistory, useLocation} from 'react-router-dom'

import {AnimatedCopyButton, RefreshIcon} from 'components'
import {DopamineAnswer} from './DopamineAssistant.types'
import './DopamineAssistantResult.css'

type DopamineAssistantResultProps = {
  answer: DopamineAnswer
  embedded?: boolean
  isRetryDisabled?: boolean
  onRetry?: () => void
  showActions?: boolean
}

const URL_SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i
const EXTERNAL_URL_PATTERN = /^(?:https?:|\/\/)/i
const MAILTO_URL_PATTERN = /^mailto:/i

const resolvesInsideApp = (
  href: string,
  currentLocation: Location,
  createHref: History['createHref'],
): boolean => {
  try {
    // history@4 decodes the pathname before React Router v5 creates its DOM
    // href. Revisit this mirror when either routing dependency is upgraded.
    const targetLocation = createLocation(
      href,
      undefined,
      undefined,
      currentLocation,
    )
    const resolvedUrl = new URL(
      createHref(targetLocation),
      window.location.href,
    )

    return resolvedUrl.origin === window.location.origin
  } catch {
    return false
  }
}

type DopamineMarkdownLinkProps = {
  children?: ReactNode
  href?: string
  title?: string
}

const DopamineMarkdownLink = ({
  children,
  href,
  title,
}: DopamineMarkdownLinkProps): ReactElement => {
  const {createHref} = useHistory()
  const currentLocation = useLocation()

  if (!href) {
    return <span title={title}>{children}</span>
  }

  // Explicit absolute and scheme-relative URLs remain external citations.
  if (EXTERNAL_URL_PATTERN.test(href)) {
    return (
      <a href={href} rel="noopener noreferrer" target="_blank" title={title}>
        {children}
      </a>
    )
  }

  const isAppPath = !href.startsWith('#') && !URL_SCHEME_PATTERN.test(href)
  if (isAppPath) {
    return resolvesInsideApp(href, currentLocation, createHref) ? (
      <Link title={title} to={href}>
        {children}
      </Link>
    ) : (
      <span title={title}>{children}</span>
    )
  }

  if (href.startsWith('#') || MAILTO_URL_PATTERN.test(href)) {
    return (
      <a href={href} title={title}>
        {children}
      </a>
    )
  }

  return <span title={title}>{children}</span>
}

const markdownComponents: Components = {
  a: DopamineMarkdownLink,
  table: ({children}) => (
    <div className="dopamine-markdown-table">
      <table>{children}</table>
    </div>
  ),
}

const DISALLOWED_ELEMENTS = ['img']
const REMARK_PLUGINS = [remarkGfm]

export const DopamineAssistantResult = ({
  answer,
  embedded = false,
  isRetryDisabled = false,
  onRetry,
  showActions = true,
}: DopamineAssistantResultProps): ReactElement | null => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const isAnswerUpdating =
    answer.status === 'streaming' || answer.status === 'awaiting-finish'

  if (answer.status === 'idle' || answer.status === 'failed') return null

  const retryAction = onRetry ? (
    <button
      aria-label={t('DOPAMINE_RETRY_ANSWER')}
      className="dopamine-answer-action dopamine-tip dopamine-tip-above relative focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent disabled:cursor-not-allowed disabled:opacity-50"
      data-tip={t('DOPAMINE_RETRY_ANSWER')}
      disabled={isRetryDisabled}
      type="button"
      onClick={onRetry}
    >
      <RefreshIcon
        classes="h-3.5 w-3.5"
        height={14}
        strokeColor="currentColor"
        width={14}
      />
      <span>{t('RETRY')}</span>
    </button>
  ) : null

  if (answer.status === 'completed' && !answer.markdown.trim()) {
    return (
      <div
        className={
          embedded
            ? 'text-sm text-gray-400'
            : 'mt-3 rounded-ds-lg rounded-tl-ds-sm border-0.5 border-white/10 bg-white/3 px-4 py-3 text-sm text-gray-400'
        }
      >
        <output>{t('DOPAMINE_EMPTY_ANSWER')}</output>
        {retryAction ? (
          <div className="mt-3 flex items-center gap-1.5 border-t-0.5 border-white/5 pt-2.5">
            {retryAction}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      aria-busy={isAnswerUpdating}
      className={
        embedded
          ? 'dopamine-markdown'
          : 'dopamine-markdown dopamine-message-surface mt-3 rounded-ds-lg rounded-tl-ds-sm px-4 py-3.5 shadow-inset-subtle'
      }
    >
      <ReactMarkdown
        components={markdownComponents}
        disallowedElements={DISALLOWED_ELEMENTS}
        remarkPlugins={REMARK_PLUGINS}
        skipHtml
        urlTransform={defaultUrlTransform}
      >
        {answer.markdown}
      </ReactMarkdown>
      {isAnswerUpdating ? (
        <output className="sr-only">{t('DOPAMINE_ANSWER_UPDATING')}</output>
      ) : null}
      {answer.status === 'streaming' ? (
        <span aria-hidden="true" className="dopamine-markdown-cursor" />
      ) : null}
      {answer.status === 'completed' && showActions ? (
        <div className="mt-3 flex items-center gap-1.5 border-t-0.5 border-white/5 pt-2.5">
          <AnimatedCopyButton
            tone="neutral"
            ariaLabel={t('DOPAMINE_COPY_ANSWER')}
            className="dopamine-answer-copy m-0 h-4 w-4"
            dataCy={`dopamine-assistant-copy-${
              answer.messageId ?? answer.runId ?? 'answer'
            }`}
            size="md"
            value={answer.markdown}
            variant="default"
          />
          {retryAction}
        </div>
      ) : null}
    </div>
  )
}
